import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import $api from 'http/index'
import Notification from 'utils/Notifications'
import {
  Icon,
  PageHeader,
  Card,
  Skeleton,
  EmptyState,
  ErrorState,
  Badge,
  UnvButton,
  UnvModal,
  DataTable,
} from 'components'

import styles from './ScheduleImportPage.module.scss'

const ScheduleImportPage = () => {
  const { t } = useTranslation('common')
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [token, setToken] = useState(null)
  const [errors, setErrors] = useState([])
  const [loading, setLoading] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [committed, setCommitted] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [errorsOpen, setErrorsOpen] = useState(false)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })
  const fileInputRef = useRef(null)

  const setNewFile = (f) => {
    setFile(f)
    setPreview(null)
    setToken(null)
    setErrors([])
    setCommitted(null)
    setProgress(0)
  }

  const onPick = () => fileInputRef.current?.click()

  const handleFile = (e) => {
    const f = e.target.files?.[0] || null
    if (f) setNewFile(f)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) setNewFile(f)
  }

  const upload = async () => {
    if (!file) return
    setLoading(true)
    setErrors([])
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await $api.post('/academic/schedule-import/preview/', fd)
      setPreview(res.data?.slots || res.data?.preview || res.data?.results || [])
      setToken(res.data?.file_token || res.data?.token || null)
      setErrors(res.data?.errors || [])
      setNotify({ isOpen: true, message: t('schedule.file_analyzed', 'Файл проанализирован'), type: 'success', sound: 'success' })
    } catch (err) {
      const msg = err?.response?.data?.detail || t('schedule.file_read_error', 'Ошибка чтения файла')
      setErrors([msg])
      setNotify({ isOpen: true, message: msg, type: 'error', sound: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const commit = async () => {
    if (!token) {
      setNotify({ isOpen: true, message: t('schedule.first_validate', 'Сначала проверьте файл'), type: 'warning', sound: 'warning' })
      return
    }
    setCommitting(true)
    setProgress(5)
    // simulated tick progress
    const tick = setInterval(() => setProgress((p) => (p < 90 ? p + 5 : p)), 200)
    try {
      const res = await $api.post('/academic/schedule-import/commit/', { file_token: token })
      const created = res.data?.created ?? res.data?.count ?? 0
      setCommitted(created)
      setProgress(100)
      setNotify({ isOpen: true, message: t('schedule.records_created', { count: created, defaultValue: 'Создано записей: {{count}}' }), type: 'success', sound: 'success' })
    } catch (err) {
      const msg = err?.response?.data?.detail || t('schedule.import_error', 'Ошибка импорта')
      setErrors([msg])
      setNotify({ isOpen: true, message: msg, type: 'error', sound: 'error' })
    } finally {
      clearInterval(tick)
      setCommitting(false)
    }
  }

  const criticalErrors = useMemo(
    () => errors.filter((e) => typeof e === 'string' ? !e.includes('warning') : !e?.warning),
    [errors]
  )

  const columns = [
    { key: 'date', label: t('schedule.col_date', 'Дата') },
    {
      key: 'time', label: t('schedule.col_time', 'Время'),
      render: (_, r) => `${r.start_time || '—'} — ${r.end_time || '—'}`
    },
    { key: 'group', label: t('schedule.col_group', 'Группа'), render: (_, r) => r.group || r.group_name || '—' },
    { key: 'subject', label: t('schedule.col_subject', 'Дисциплина'), render: (_, r) => r.subject || r.subject_name || '—' },
    { key: 'teacher', label: t('schedule.col_teacher', 'Преподаватель'), render: (_, r) => r.teacher || r.teacher_name || '—' },
    { key: 'room', label: t('schedule.col_room', 'Аудитория'), render: (_, r) => r.room || r.room_name || '—' },
    { key: 'kind', label: t('schedule.col_type', 'Тип'), render: (v) => v || '—' },
    {
      key: 'conflict', label: t('schedule.col_conflict', 'Конфликт'),
      render: (_, r) => r.conflict || r.conflicts?.length ? <Badge variant="danger" size="sm">{t('schedule.conflict', 'Конфликт')}</Badge> : <Badge variant="success" size="sm">OK</Badge>
    },
  ]

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('schedule.import_title', 'Импорт расписания')}
        subtitle={t('schedule.import_subtitle', 'Загрузка расписания из Excel-файла')}
      />

      {/* Upload zone */}
      <div
        className={[styles.dropZone, dragOver ? styles.dropZoneOver : '', file ? styles.dropZoneFilled : ''].filter(Boolean).join(' ')}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={onPick}
        role="button"
        tabIndex={0}
        aria-label={t('schedule.drop_file', 'Перетащите файл или нажмите чтобы выбрать')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onPick() }}
      >
        <Icon name="upload" size={42} color="#FF7A00" />
        <div className={styles.dropTitle}>
          {file ? file.name : t('schedule.drop_excel', 'Перетащите Excel-файл или нажмите')}
        </div>
        <div className={styles.dropSub}>
          {file ? `${(file.size / 1024).toFixed(1)} КБ` : t('schedule.drop_format', 'Поддерживается формат .xlsx')}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFile}
          className={styles.hiddenInput}
        />
      </div>

      {/* Action row */}
      <Card padding="md" className={styles.actions}>
        <UnvButton variant="primary" iconLeft="check-circle" onClick={upload} disabled={!file || loading} loading={loading}>
          {t('schedule.import_check', 'Проверить файл')}
        </UnvButton>
        <UnvButton
          variant="success"
          iconLeft="upload"
          onClick={commit}
          disabled={!token || committing || criticalErrors.length > 0}
          loading={committing}
        >
          {t('schedule.import_do', 'Импортировать')}
        </UnvButton>
        {errors.length > 0 && (
          <UnvButton variant="danger" iconLeft="alert-circle" onClick={() => setErrorsOpen(true)}>
            {t('schedule.show_errors', 'Ошибки')} ({errors.length})
          </UnvButton>
        )}
        {committed != null && (
          <Badge variant="success" dot>{t('schedule.import_done', { count: committed, defaultValue: 'Импорт завершён ({{count}})' })}</Badge>
        )}
      </Card>

      {/* Progress */}
      {committing && (
        <div className={styles.progressBar} role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>
      )}

      {/* Preview table */}
      <Card padding="none" className={styles.previewCard}>
        {loading ? (
          <Skeleton variant="table" rows={6} cols={7} />
        ) : !preview ? (
          <EmptyState
            iconName="file-text"
            title={t('schedule.file_not_loaded', 'Файл не загружен')}
            description={t('schedule.file_hint', 'Перетащите Excel-файл в зону выше для предпросмотра')}
          />
        ) : preview.length === 0 ? (
          <EmptyState iconName="file-text" title={t('schedule.no_records_found', 'Записи не найдены')} description="" />
        ) : (
          <DataTable columns={columns} data={preview.map((p, i) => ({ id: p.id ?? i, ...p }))} pageSize={20} />
        )}
      </Card>

      {/* Errors modal */}
      <UnvModal open={errorsOpen} onClose={() => setErrorsOpen(false)} title={t('schedule.validation_errors', 'Ошибки валидации')} size="md">
        <ul className={styles.errList}>
          {errors.map((e, i) => (
            <li key={i}>
              <Icon name="alert-circle" size={14} />
              {typeof e === 'string' ? e : (e.message || JSON.stringify(e))}
            </li>
          ))}
        </ul>
        <UnvModal.Footer>
          <UnvButton variant="primary" onClick={() => setErrorsOpen(false)}>{t('common.close', 'Закрыть')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default ScheduleImportPage
